import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SITE_DEFAULTS } from "@/lib/siteDefaults";

const SiteContext = createContext(null);
const CACHE_KEY = "gavra_site_content_v1";

function deepMerge(base, override) {
  if (Array.isArray(override)) return override;
  if (base && override && typeof base === "object" && typeof override === "object" && !Array.isArray(base)) {
    const out = { ...base };
    for (const k of Object.keys(override)) out[k] = deepMerge(base[k], override[k]);
    return out;
  }
  return override === undefined ? base : override;
}

function readCache() {
  try {
    const s = localStorage.getItem(CACHE_KEY);
    if (!s) return null;
    // merge over defaults so a stale/partial cache never yields undefined fields
    return deepMerge(SITE_DEFAULTS, JSON.parse(s));
  } catch (e) {
    return null;
  }
}

export function SiteProvider({ children }) {
  const cached = readCache();
  // Start from last-known content (cache) to avoid a flash of default text; fall back to defaults.
  const [content, setContent] = useState(cached || SITE_DEFAULTS);
  const [loaded, setLoaded] = useState(Boolean(cached));

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      if (data) {
        setContent(data);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) {}
      }
    } catch (e) {
      // keep cached/defaults on failure
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return <SiteContext.Provider value={{ content, refresh, loaded }}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const ctx = useContext(SiteContext);
  return ctx ? ctx.content : SITE_DEFAULTS;
}

export function useSiteLoaded() {
  const ctx = useContext(SiteContext);
  return ctx ? ctx.loaded : true;
}

export function useSiteRefresh() {
  const ctx = useContext(SiteContext);
  return ctx ? ctx.refresh : async () => {};
}
